import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Button, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, IconButton,
    CircularProgress, useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    Close as CloseIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { colorUtils } from '../theme';
import {
    ExtendedTicket, RootState,
    TicketStatus, TicketPriority, TicketCategory,
} from '../types';
import { IntegrationAlert } from '../types/integration';
import { updateTicket, removeAlertsFromTicket, resolveTicket } from '../store/slices/ticketsSlice';
import { fetchAssignableUsers } from '../store/slices/usersSlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TicketsService } from '../services/ticketsService';
import { AISuggestion } from '../types';

interface EditTicketModalProps {
    open: boolean;
    onClose: () => void;
    ticket: ExtendedTicket;
    integrationAlerts: IntegrationAlert[];
    resolveMode?: boolean;
}

const EditTicketModal: React.FC<EditTicketModalProps> = ({ open, onClose, ticket, integrationAlerts, resolveMode = false }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const users = useSelector((state: RootState) => state.users.users);
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const { isLoading } = useSelector((state: RootState) => state.tickets);

    // Form state
    const [title, setTitle] = useState(ticket.title);
    const [description, setDescription] = useState(ticket.description);
    const [status, setStatus] = useState<TicketStatus>(ticket.meta.status);
    const [priority, setPriority] = useState<TicketPriority>(ticket.meta.priority);
    const [category, setCategory] = useState<TicketCategory>(ticket.meta.category);
    const [assigneeId, setAssigneeId] = useState<string>(ticket.meta.assignee?.id || '');
    const [clientRecommendations, setClientRecommendations] = useState<string>(ticket.client_recommendations || '');

    // Resolution fields (resolve mode)
    const [rootCause, setRootCause] = useState<string>((ticket as any).root_cause || '');
    const [mitreTags, setMitreTags] = useState<string[]>((ticket as any).mitre_tags || []);
    const [tagInput, setTagInput] = useState('');

    // Local alerts state (for optimistic removal)
    const [localAlerts, setLocalAlerts] = useState<IntegrationAlert[]>(integrationAlerts);

    // Confirmation dialog state
    const [confirmRemoveAlert, setConfirmRemoveAlert] = useState<IntegrationAlert | null>(null);

    // Re-sync form when ticket/alerts change
    useEffect(() => {
        if (open) {
            setTitle(ticket.title);
            setDescription(ticket.description);
            setStatus(ticket.meta.status);
            setPriority(ticket.meta.priority);
            setCategory(ticket.meta.category);
            setAssigneeId(ticket.meta.assignee?.id || '');
            setClientRecommendations(ticket.client_recommendations || '');
            setRootCause((ticket as any).root_cause || '');
            setMitreTags((ticket as any).mitre_tags || []);
            setTagInput('');
            setLocalAlerts(integrationAlerts);
        }
    }, [open, ticket, integrationAlerts]);

    // In resolve mode: merge ticket.mitre_tags with any pending/accepted AI suggestion
    useEffect(() => {
        if (!open || !resolveMode) return;
        TicketsService.getAISuggestions(ticket.id)
            .then(({ suggestions }: { suggestions: AISuggestion[] }) => {
                const mitreSuggestion = suggestions.find((s: AISuggestion) => s.suggestion_type === 'mitre_tags');
                const suggestedTags: string[] = mitreSuggestion?.content?.tags ?? [];
                const existing: string[] = (ticket as any).mitre_tags ?? [];
                const merged = Array.from(new Set([...existing, ...suggestedTags]));
                if (merged.length > 0) setMitreTags(merged);
            })
            .catch(() => {});
    }, [open, resolveMode, ticket.id]);

    // Fetch assignable analysts whenever the modal opens
    useEffect(() => {
        if (open) {
            dispatch(fetchAssignableUsers() as any);
        }
    }, [open, dispatch]);

    const handleSave = async () => {
        const changes: Record<string, any> = {};
        if (title !== ticket.title) changes.title = title;
        if (description !== ticket.description) changes.description = description;
        if (status !== ticket.meta.status) changes.status = status;
        if (priority !== ticket.meta.priority) changes.priority = priority;
        if (category !== ticket.meta.category) changes.category = category;
        const originalAssigneeId = ticket.meta.assignee?.id || '';
        if (assigneeId !== originalAssigneeId) changes.assignee_id = assigneeId || null;
        const originalRecs = ticket.client_recommendations || '';
        if (clientRecommendations !== originalRecs) changes.client_recommendations = clientRecommendations || null;

        if (Object.keys(changes).length === 0) {
            onClose();
            return;
        }

        try {
            await (dispatch(updateTicket({ ticketId: ticket.id, ticketData: changes }) as any)).unwrap();
            onClose();
        } catch {
            // Error is handled by Redux state
        }
    };

    const handleRemoveAlert = async (alert: IntegrationAlert) => {
        setConfirmRemoveAlert(null);
        try {
            await (dispatch(removeAlertsFromTicket({ ticketId: ticket.id, alertIds: [alert.id] }) as any)).unwrap();
            setLocalAlerts(prev => prev.filter(a => a.id !== alert.id));
        } catch {
            // Error is handled by Redux state
        }
    };

    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
            e.preventDefault();
            const tag = tagInput.trim().toUpperCase();
            if (!mitreTags.includes(tag)) setMitreTags(prev => [...prev, tag]);
            setTagInput('');
        }
    };

    const handleSaveAndResolve = async () => {
        // Commit any tag still sitting in the text input (user typed but didn't press Enter)
        const finalTags = [...mitreTags];
        if (tagInput.trim()) {
            const pending = tagInput.trim().toUpperCase();
            if (!finalTags.includes(pending)) finalTags.push(pending);
        }

        const changes: Record<string, any> = {};
        if (title !== ticket.title) changes.title = title;
        if (description !== ticket.description) changes.description = description;
        if (priority !== ticket.meta.priority) changes.priority = priority;
        if (category !== ticket.meta.category) changes.category = category;
        const originalAssigneeId = ticket.meta.assignee?.id || '';
        if (assigneeId !== originalAssigneeId) changes.assignee_id = assigneeId || null;
        const originalRecs = ticket.client_recommendations || '';
        if (clientRecommendations !== originalRecs) changes.client_recommendations = clientRecommendations || null;

        try {
            if (Object.keys(changes).length > 0) {
                await (dispatch(updateTicket({ ticketId: ticket.id, ticketData: changes }) as any)).unwrap();
            }
            await (dispatch(resolveTicket({ ticketId: ticket.id, comment: rootCause.trim(), mitreTags: finalTags }) as any)).unwrap();
            onClose();
        } catch {
            // Errors handled by Redux state
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <DialogTitle sx={{
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.border.light}`,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}>
                    {resolveMode && <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.severity.low }} />}
                    {resolveMode ? 'Review & Resolve' : 'Edit Ticket'}
                    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.tertiary, mt: 0.5, ml: 'auto' }}>
                        {ticket.id}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ pt: 3 }}>
                    {/* Section 1: Linked Alerts */}
                    <Box sx={{ mb: 3, mt: 2 }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 1.5,
                        }}>
                            LINKED ALERTS
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {localAlerts.map(alert => (
                                <Chip
                                    key={alert.id}
                                    label={`${alert.id}`}
                                    size="small"
                                    onDelete={localAlerts.length > 1 ? () => setConfirmRemoveAlert(alert) : undefined}
                                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        justifyContent: 'space-between',
                                        maxWidth: '100%',
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.08),
                                        color: theme.palette.text.secondary,
                                        border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.19)}`,
                                        '& .MuiChip-deleteIcon': {
                                            color: theme.palette.text.tertiary,
                                            '&:hover': { color: theme.palette.error.main },
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Section 2: Ticket Fields */}
                    <Box>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 1.5,
                        }}>
                            TICKET FIELDS
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme.palette.background.default,
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.text.primary,
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    multiline
                                    rows={3}
                                    disabled={isLoading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme.palette.background.default,
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.text.primary,
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={status}
                                        label="Status"
                                        onChange={(e) => setStatus(e.target.value as TicketStatus)}
                                        disabled={isLoading}
                                    >
                                        {Object.values(TicketStatus).map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        value={priority}
                                        label="Priority"
                                        onChange={(e) => setPriority(e.target.value as TicketPriority)}
                                        disabled={isLoading}
                                    >
                                        {Object.values(TicketPriority).map((p) => (
                                            <MenuItem key={p} value={p}>
                                                {p.charAt(0).toUpperCase() + p.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={category}
                                        label="Category"
                                        onChange={(e) => setCategory(e.target.value as TicketCategory)}
                                        disabled={isLoading}
                                    >
                                        {Object.values(TicketCategory).map((c) => (
                                            <MenuItem key={c} value={c}>
                                                {c.charAt(0).toUpperCase() + c.slice(1).replace('_', ' ')}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Assignee</InputLabel>
                                    <Select
                                        value={assigneeId}
                                        label="Assignee"
                                        onChange={(e) => setAssigneeId(e.target.value as string)}
                                        disabled={isLoading}
                                    >
                                        <MenuItem value="">
                                            <em>Unassigned</em>
                                        </MenuItem>
                                        {users.map((user) => (
                                            <MenuItem key={user.id} value={user.id}>
                                                {user.first_name} {user.last_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Section 3: Notes for client */}
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 1.5,
                        }}>
                            NOTES FOR CLIENT
                        </Typography>
                        <TextField
                            fullWidth
                            label="Recommended actions"
                            placeholder="What should the client do? e.g. Reset passwords for affected accounts, isolate the affected machine..."
                            value={clientRecommendations}
                            onChange={(e) => setClientRecommendations(e.target.value)}
                            multiline
                            rows={3}
                            disabled={isLoading}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: theme.palette.background.default,
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme.palette.text.primary,
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, mt: 0.5, display: 'block' }}>
                            Visible to the client on their incident detail page.
                        </Typography>
                    </Box>

                    {/* Section 4: Resolution (resolve mode only) */}
                    {resolveMode && (
                        <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.border.light}` }}>
                            <Typography variant="caption" sx={{
                                color: theme.palette.severity.low,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                display: 'block',
                                mb: 1.5,
                            }}>
                                RESOLUTION *
                            </Typography>

                            <TextField
                                fullWidth
                                label="Root cause & resolution summary"
                                placeholder="What happened? What was the root cause? How was it contained?"
                                value={rootCause}
                                onChange={(e) => setRootCause(e.target.value)}
                                multiline
                                rows={3}
                                required
                                disabled={isLoading}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme.palette.background.default,
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.severity.low,
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />

                            <Box>
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, display: 'block', mb: 0.75 }}>
                                    MITRE ATT&CK TAGS — pre-populated from AI analysis, remove any that don't apply
                                </Typography>
                                {mitreTags.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                                        {mitreTags.map((tag) => (
                                            <Chip
                                                key={tag}
                                                label={tag}
                                                size="small"
                                                onDelete={() => setMitreTags(prev => prev.filter(t => t !== tag))}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1),
                                                    color: theme.palette.primary.main,
                                                    border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.25)}`,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add custom tag and press Enter…"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={handleTagKeyDown}
                                    disabled={isLoading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme.palette.background.default,
                                            fontSize: '0.82rem',
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    borderTop: `1px solid ${theme.palette.border.light}`,
                    p: 2,
                }}>
                    <Button
                        onClick={onClose}
                        disabled={isLoading}
                        sx={{
                            color: theme.palette.text.secondary,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>
                    {resolveMode ? (
                        <Button
                            onClick={handleSaveAndResolve}
                            variant="contained"
                            disabled={isLoading || !title.trim() || !rootCause.trim() || (mitreTags.length === 0 && !tagInput.trim())}
                            startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: theme.palette.severity.low,
                                '&:hover': { backgroundColor: theme.palette.success.dark },
                            }}
                        >
                            {isLoading ? <CircularProgress size={20} /> : 'Save & Resolve'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={isLoading || !title.trim()}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {isLoading ? <CircularProgress size={20} /> : 'Save Changes'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Confirmation dialog for alert removal */}
            <Dialog
                open={!!confirmRemoveAlert}
                onClose={() => setConfirmRemoveAlert(null)}
                maxWidth="xs"
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <WarningIcon sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1.5 }} />
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                        Remove alert from ticket?
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {confirmRemoveAlert?.id.slice(0, 8)}... ({confirmRemoveAlert?.integration_type})
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmRemoveAlert(null)}
                        sx={{
                            color: theme.palette.text.secondary,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => confirmRemoveAlert && handleRemoveAlert(confirmRemoveAlert)}
                        variant="contained"
                        color="error"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditTicketModal;
